import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"
import { cn } from "@/lib/utils"
import { useToolTheme } from "@/contexts/ThemeContext"

const buttonVariants = cva(
  "inline-flex items-center justify-center whitespace-nowrap rounded-lg text-sm font-medium ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 active:scale-[0.98]",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground shadow-lg hover:opacity-90",
        destructive: "bg-destructive text-destructive-foreground shadow-lg hover:opacity-90",
        outline: "border-2 bg-background shadow-sm hover:bg-accent hover:text-accent-foreground",
        secondary: "bg-secondary text-secondary-foreground shadow-md hover:opacity-90",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        link: "text-primary underline-offset-4 hover:underline",
      },
      size: {
        default: "h-11 px-6 py-2",
        sm: "h-9 rounded-md px-4",
        lg: "h-12 rounded-lg px-8 text-base",
        icon: "h-11 w-11",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const { toolColors } = useToolTheme();
    const Comp = asChild ? Slot : "button"
    
    // Custom styles based on current tool theme
    const customStyles: React.CSSProperties = {};
    
    if (variant === 'default') {
      customStyles.backgroundColor = toolColors.primary;
      customStyles.color = 'white';
      customStyles.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.1), 0 4px 6px rgba(0, 0, 0, 0.05)';
    } else if (variant === 'outline') {
      customStyles.borderColor = toolColors.primary;
      customStyles.color = toolColors.primary;
      customStyles.backgroundColor = 'white';
      customStyles.boxShadow = '0 1px 3px rgba(0, 0, 0, 0.05)';
    } else if (variant === 'link') {
      customStyles.color = toolColors.primary;
    } else if (variant === 'secondary') {
      customStyles.backgroundColor = 'white';
      customStyles.color = toolColors.primary;
      customStyles.boxShadow = '0 1px 3px rgba(0, 0, 0, 0.1)';
    } else if (variant === 'ghost') {
      customStyles.color = toolColors.primary;
    }
    
    // Add hover effects
    customStyles['--hover-bg'] = `${toolColors.primary}10`;
    customStyles['--hover-border'] = toolColors.primary;
    customStyles['--hover-shadow'] = '0 4px 8px rgba(0, 0, 0, 0.1), 0 2px 4px rgba(0, 0, 0, 0.05)';
    
    // Add focus styles
    customStyles['--focus-ring'] = `${toolColors.primary}40`;
    
    // Add active styles
    customStyles['--active-scale'] = '0.98';
    customStyles['--active-shadow'] = '0 1px 2px rgba(0, 0, 0, 0.1)';
    
    return (
      <Comp
        className={cn(
          buttonVariants({ variant, size, className }),
          "transition-all duration-200 ease-out",
          "hover:transform hover:-translate-y-0.5",
          "active:transform active:translate-y-0",
          "focus:ring-2 focus:ring-offset-2",
          "disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
        )}
        ref={ref}
        style={customStyles}
        {...props}
      />
    )
  }
)
Button.displayName = "Button"

export { Button, buttonVariants }